import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import DataTable, {
  CellPrimary,
  ActionButtons,
  EyeIcon,
} from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import YearSelect from 'components/FormFields/YearSelect';
import PageContainer from 'components/PageContainer';
import { HeaderContainer, HeaderTitle } from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';
import PageToolbar from 'components/PageToolbar';
import SearchInput from 'components/SearchInput';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import PermissionModal from 'pages/track/PermissionModal/PermissionModal';
import { AppContext } from 'shared/AppContext';
import SelectUserModal from '../TrackOpenCredit/components/SelectUserModal';
import { useGetUserRedeemedCreditsByAccount } from './gql/get-user-redeemed-credits-by-account';

const TrackRedeemedCreditsDetail = () => {
  const [credits, setCredits] = useState([]);
  const [count, setCount] = useState(0);
  const [showIssueCredit, setShowIssueCredit] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  const { pathname } = useLocation();

  // Context-aware routing
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const {
    state: { capabilities },
  } = useContext(AppContext);

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Year filter - use URL param directly
  const thisYear = Number(moment().format('YYYY'));
  const yearFilter = input.year ? Number(input.year) : thisYear;

  const handleYearChange = (e) => {
    const newYear = Number(e.target.value);
    updateQuery({ year: newYear, page: 1 });
  };

  // Fetch credits data
  const { res, loading } = useGetUserRedeemedCreditsByAccount();

  useEffect(() => {
    if (res && res.getUserRedeemedCreditsByAccount) {
      setCredits(res.getUserRedeemedCreditsByAccount.results || []);
      setCount(res.getUserRedeemedCreditsByAccount.count || 0);
    }
  }, [res]);

  // Handle search
  const handleSearch = useCallback(
    (searchTerm) => {
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  // Handle pagination
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(count / 15);
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // DataTable columns configuration
  const columns = [
    {
      field: 'user',
      header: 'Ticket Holder',
      mobileTitle: true,
      render: (credit) => (
        <CellPrimary>{credit.user?.formatted_name}</CellPrimary>
      ),
    },
    {
      field: 'amount',
      header: 'Credit',
      mobileSubtitle: true,
      render: (credit) => credit.amount,
    },
    {
      field: 'id',
      header: 'Credit #',
    },
    {
      field: 'date',
      header: 'Used',
      render: (credit) =>
        moment(credit.date, 'MM-DD-YYYY')
          .format('ddd MMM D - YYYY')
          .toUpperCase(),
    },
  ];

  // Custom actions renderer - only show transaction link if transaction exists
  const actionsRenderer = (credit) => {
    if (!credit.transaction?.id) {
      return null;
    }
    return (
      <ActionButtons>
        <ButtonV2
          variant="icon"
          icon={<EyeIcon />}
          title="View Transaction"
          as={Link}
          to={`${routePrefix}/transactions/detail/${credit.transaction.id}`}
        />
      </ActionButtons>
    );
  };

  const totalPages = Math.ceil(count / 15);

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="credits" />
            <HeaderTitle>Redeemed Credits</HeaderTitle>
          </HeaderContainer>
        </Card>

        {/* Content Card */}
        <Card noPadding>
          <ContentWrapper>
            {/* Page Header with Search */}
            <PageToolbar>
              <SearchInput
                onSearch={handleSearch}
                placeholder="Search by Ticket Holder"
                debounceMs={500}
              />
              <YearSelect value={yearFilter} onChange={handleYearChange} />
              <ButtonV2
                onClick={() =>
                  !capabilities || capabilities?.issue_credits
                    ? setShowIssueCredit(true)
                    : setShowPermissionModal(true)
                }
                style={{ width: 'auto', flexShrink: 0 }}
              >
                Issue Credit
              </ButtonV2>
            </PageToolbar>

            {/* DataTable */}
            <DataTable
              columns={columns}
              data={credits}
              loading={loading}
              keyField="id"
              emptyMessage="No redeemed credits found"
              emptySubtext="Try adjusting your search or filters"
              actionsRenderer={actionsRenderer}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </ContentWrapper>
        </Card>

        {/* Select User Modal for Issue Credit */}
        <SelectUserModal
          isOpen={showIssueCredit}
          onClose={() => setShowIssueCredit(false)}
        />

        {/* Permission Modal */}
        <PermissionModal
          isVisible={showPermissionModal}
          hideModal={() => setShowPermissionModal(false)}
        />
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default TrackRedeemedCreditsDetail;
